import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd, removeWillChangeOnAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 * @property {HTMLDivElement} menuDrawer - The slideable drawer panel containing the menu.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details', 'menuDrawer'];

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();
    this.#setupTextOnlyMenuItems();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
  }

  /**
   * Keeps the mobile "Ver todo SOL" entry as a full-width text link while
   * preserving collection images for the other SOL child links.
   */
  #setupTextOnlyMenuItems() {
    const textOnlyLink = this.querySelector('#HeaderDrawer-sol-ver-todo-sol');

    if (!(textOnlyLink instanceof HTMLElement)) {
      console.warn('[HeaderDrawer] No se encontró Ver todo SOL');
      return;
    }

    console.log('[HeaderDrawer] Ver todo SOL encontrado:', textOnlyLink);

    const listItem = textOnlyLink.closest('.menu-drawer__list-item');

    if (!(listItem instanceof HTMLElement)) {
      console.warn(
        '[HeaderDrawer] No se encontró .menu-drawer__list-item',
        textOnlyLink.parentElement
      );
      return;
    }

    console.log('[HeaderDrawer] List item encontrado:', listItem);

    listItem.style.setProperty('grid-column', '1 / -1');
    listItem.style.setProperty('width', '100%');

    textOnlyLink.style.setProperty('display', 'flex');
    textOnlyLink.style.setProperty('flex-direction', 'row');
    textOnlyLink.style.setProperty('align-items', 'center');
    textOnlyLink.style.setProperty('justify-content', 'flex-start');
    textOnlyLink.style.setProperty('padding', 'var(--padding-2xs) 0');
    textOnlyLink.style.setProperty('width', '100%');

    if (!textOnlyLink.querySelector(':scope > .menu-drawer__link-image')) {
      const marker = document.createElement('span');
      marker.className = 'menu-drawer__link-image';
      marker.hidden = true;
      marker.setAttribute('aria-hidden', 'true');
      textOnlyLink.prepend(marker);
    }
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {string} [target]
   * @param {Event} [event]
   */
open(target, event) {
  const details = this.#getDetailsElement(event);
  const summary = details.querySelector('summary');

  if (!summary) return;

  summary.setAttribute('aria-expanded', 'true');

  this.preventInitialAccordionAnimations(details);

  // Volvemos a preparar los elementos después de que el drawer exista
  // completamente en el DOM.
  this.#setupTextOnlyMenuItems();

  requestAnimationFrame(() => {
    // Segunda ejecución por si Shopify terminó de renderizar
    // el contenido durante el siguiente frame.
    this.#setupTextOnlyMenuItems();

    details.classList.add('menu-open');

    if (target) {
      this.refs.menuDrawer.classList.add(
        'menu-drawer--has-submenu-opened'
      );
    }

    const drawer = details.querySelector(
      '.menu-drawer, .menu-drawer__submenu'
    );

    onAnimationEnd(
      drawer || details,
      () => trapFocus(details),
      { subtree: false }
    );
  });
}

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');
    this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');

    // Wait for the .menu-drawer element's transition, not the entire details subtree
    // This avoids waiting for child accordion/resource-card animations which can cause issues on Firefox
    const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');

    onAnimationEnd(
      drawer || details,
      () => {
        reset(details);
        if (details === this.refs.details) {
          removeTrapFocus();
          const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
          openDetails.forEach(reset);
        } else {
          trapFocus(this.refs.details);
        }
      },
      { subtree: false }
    );
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
